import { trpc } from '@/utils/trpc';
import { useQuery } from '@tanstack/react-query';
import {
  labRecordType,
  ProcedureRecordType,
  SourceType,
  UnifiedAdditionalRecordClassificationType,
} from '@/feature/patients/types';

export type MedicalRecordSortColumn =
  | 'sourceType'
  | 'dateUploaded'
  | 'dateOfStudy';
export type SortOrder = 'asc' | 'desc';

export interface MedicalRecordsSortColumn {
  column: MedicalRecordSortColumn;
  order?: SortOrder;
}

export interface MedicalRecordsPagination {
  offset: number;
  limit: number;
}

export interface MedicalRecordsQueryParams {
  patientId: string;
  pagination?: MedicalRecordsPagination;
  sortColumns?: MedicalRecordsSortColumn[];
}

export const useGetPatientMedicalRecordsCounts = (patientId?: string) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientMedicalRecordsCounts.queryOptions({
      patientId: patientId || '',
    }),
    enabled: !!patientId,
    meta: { errorMessage: 'Failed to fetch medical records counts' },
  });

export const useGetPatientProviderNotesSummaries = (
  params?: MedicalRecordsQueryParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientProviderNotesSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Provider/Facility Notes' },
  });

export interface ProviderNotesDetailsParams {
  patientId?: string;
  providerNoteId?: string;
  sourceType?: SourceType;
}

export const useGetPatientMedicalRecordsProviderNotesDetails = (
  params: ProviderNotesDetailsParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientProviderNotesDetails.queryOptions({
      patientId: params.patientId || '',
      providerNoteId: params.providerNoteId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
    }),
    enabled:
      !!params.patientId && !!params.providerNoteId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Provider Note Details' },
  });

export const useGetPatientImagingSummaries = (
  params?: MedicalRecordsQueryParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientImagingSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Imaging' },
  });

export interface ImagingDetailsParams {
  patientId?: string;
  imagingId?: string;
  sourceType?: SourceType;
}

export const useGetPatientMedicalRecordsImagingDetails = (
  params: ImagingDetailsParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientImagingDetails.queryOptions({
      patientId: params.patientId || '',
      imagingId: params.imagingId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
    }),
    enabled: !!params.patientId && !!params.imagingId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Imaging Details' },
  });

export const useGetPatientProceduresSummaries = (
  params?: MedicalRecordsQueryParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientProceduresSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Procedures' },
  });

export interface ProceduresDetailsParams {
  patientId?: string;
  procedureId?: string;
  sourceType?: SourceType;
  recordType?: ProcedureRecordType;
}

export const useGetPatientMedicalRecordsProceduresDetails = (
  params: ProceduresDetailsParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientProceduresDetails.queryOptions({
      patientId: params.patientId || '',
      procedureId: params.procedureId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
      recordType: params.recordType,
    }),
    enabled: !!params.patientId && !!params.procedureId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Procedure Details' },
  });

export const useGetPatientLabResultsSummaries = (
  params?: MedicalRecordsQueryParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientLabResultsSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Lab Results' },
  });

export interface LabResultsDetailsParams {
  patientId?: string;
  labId?: string;
  sourceType?: SourceType;
  recordType?: labRecordType;
}

export const useGetPatientMedicalRecordsLabResultsDetails = (
  params: LabResultsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientLabResultsDetails.queryOptions({
      patientId: params.patientId || '',
      labId: params.labId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
      recordType: params.recordType,
    }),
    enabled: !!params.patientId && !!params.labId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Lab Result Details' },
  });

export const useGetPatientPathologyResultsSummaries = (
  params?: MedicalRecordsQueryParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientPathologyResultsSummaries.queryOptions(
      {
        patientId: params?.patientId || '',
        pagination: params?.pagination,
        sortColumns: params?.sortColumns,
      },
    ),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Pathology Results' },
  });

export interface PathologyResultsDetailsParams {
  patientId?: string;
  pathologyId?: string;
  sourceType?: SourceType;
}

export const useGetPatientMedicalRecordsPathologyResultsDetails = (
  params: PathologyResultsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientPathologyResultsDetails.queryOptions(
      {
        patientId: params.patientId || '',
        pathologyId: params.pathologyId || '',
        sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
      },
    ),
    enabled: !!params.patientId && !!params.pathologyId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Pathology Result Details' },
  });

export const useGetPatientMedicationsSummaries = (
  params?: MedicalRecordsQueryParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientMedicationsSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Medications' },
  });

export interface MedicationsDetailsParams {
  patientId?: string;
  medicationId?: string;
  sourceType?: SourceType;
}

export const useGetPatientMedicalRecordsMedicationsDetails = (
  params: MedicationsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientMedicationsDetails.queryOptions({
      patientId: params.patientId || '',
      medicationId: params.medicationId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
    }),
    enabled: !!params.patientId && !!params.medicationId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Medication Details' },
  });

export const useGetPatientVitalsSummaries = (
  params?: MedicalRecordsQueryParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientVitalsSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Vitals' },
  });

export interface VitalsDetailsParams {
  patientId?: string;
  vitalId?: string;
  sourceType?: SourceType;
}

export const useGetPatientMedicalRecordsVitalsDetails = (
  params: VitalsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientVitalsDetails.queryOptions({
      patientId: params.patientId || '',
      vitalId: params.vitalId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
    }),
    enabled: !!params.patientId && !!params.vitalId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Vital Details' },
  });

export const useGetPatientProblemsSummaries = (
  params?: MedicalRecordsQueryParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientProblemsSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Problems' },
  });

export interface ProblemsDetailsParams {
  patientId?: string;
  problemId?: string;
  sourceType?: SourceType;
}

export const useGetPatientMedicalRecordsProblemsDetails = (
  params: ProblemsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientProblemsDetails.queryOptions({
      patientId: params.patientId || '',
      problemId: params.problemId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
    }),
    enabled: !!params.patientId && !!params.problemId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Problem Details' },
  });

export const useGetPatientEncountersSummaries = (
  params?: MedicalRecordsQueryParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientEncountersSummaries.queryOptions({
      patientId: params?.patientId || '',
      pagination: params?.pagination,
      sortColumns: params?.sortColumns,
    }),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Encounters' },
  });

export interface EncounterDetailsParams {
  patientId?: string;
  encounterId?: string;
  sourceType?: SourceType;
}

export const useGetPatientMedicalRecordsEncounterDetails = (
  params: EncounterDetailsParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientEncountersDetails.queryOptions({
      patientId: params.patientId || '',
      encounterId: params.encounterId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
    }),
    enabled: !!params.patientId && !!params.encounterId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Encounter Details' },
  });

export const useGetPatientAdditionalRecordsSummaries = (
  params?: MedicalRecordsQueryParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientAdditionalRecordsSummaries.queryOptions(
      {
        patientId: params?.patientId || '',
        pagination: params?.pagination,
        sortColumns: params?.sortColumns,
      },
    ),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Additional Records' },
  });

export const useGetPatientOriginalRecordsSummaries = (
  params?: MedicalRecordsQueryParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientOriginalRecordsSummaries.queryOptions(
      {
        patientId: params?.patientId || '',
        pagination: params?.pagination,
        sortColumns: params?.sortColumns,
      },
    ),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch Original Records' },
  });

export interface AdditionalRecordsDetailsParams {
  patientId?: string;
  additionalRecordId?: string;
  classificationType?: UnifiedAdditionalRecordClassificationType;
  sourceType?: SourceType;
}

export const useGetPatientMedicalRecordsAdditionalRecordsDetails = (
  params: AdditionalRecordsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientAdditionalRecordsDetails.queryOptions(
      {
        patientId: params.patientId || '',
        additionalRecordId: params.additionalRecordId || '',
        classificationType:
          params.classificationType ??
          UnifiedAdditionalRecordClassificationType.UNSPECIFIED,
        sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
      },
    ),
    enabled:
      !!params.patientId &&
      !!params.additionalRecordId &&
      !!params.classificationType &&
      !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Additional Record Details' },
  });

export interface OriginalRecordsDetailsParams {
  patientId?: string;
  originalRecordId?: string;
  sourceType?: SourceType;
}

export const useGetPatientMedicalRecordsOriginalRecordsDetails = (
  params: OriginalRecordsDetailsParams,
) =>
  useQuery({
    ...trpc.medicalRecordsRouter.getPatientOriginalRecordsDetails.queryOptions({
      patientId: params.patientId || '',
      originalRecordId: params.originalRecordId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
    }),
    enabled:
      !!params.patientId && !!params.originalRecordId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch Original Record Details' },
  });
